import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import {
  pickEssLeaveEntryTypeId,
  useEssDashboard,
  useEssLeaveApply,
  useEssLeaveMyRequests,
  useEssLeaveRecall,
  useEssLeaveRemind,
  useEssLeaveReportingSeniors,
} from "../../services/essService";
import { FiBell, FiRotateCcw } from "react-icons/fi";

const formatCellDate = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(v);
  }
};

const formatReminderAt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
};

const statusColor = (s) => {
  const x = String(s || "").toLowerCase();
  if (x === "approved") return "green";
  if (x === "rejected") return "red";
  if (x === "pending") return "yellow";
  if (x === "recalled") return "gray";
  return "gray";
};

const EssLeave = () => {
  const { essDashboardIsLoading, portal } = useEssDashboard();
  const leaveEntryTypes = portal?.leave_entry_types;
  const canApplyLeave = portal?.actions?.can_apply_leave !== false;
  const { reportingSeniors, reportingSeniorsIsLoading } = useEssLeaveReportingSeniors({
    enabled: canApplyLeave,
  });
  const { leaveRequests, leaveRequestsIsLoading, refetchLeaveRequests } = useEssLeaveMyRequests();
  const { leaveApply, leaveApplyIsLoading } = useEssLeaveApply();
  const remindMutation = useEssLeaveRemind();
  const recallMutation = useEssLeaveRecall();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [reportingSeniorId, setReportingSeniorId] = useState("");

  const leaveTypeId = pickEssLeaveEntryTypeId(leaveEntryTypes);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canApplyLeave) return;
    if (!leaveTypeId) return;
    if (!startDate || !endDate) return;
    const payload = {
      entry_type: leaveTypeId,
      leave_start: `${startDate}T00:00:00`,
      leave_end: `${endDate}T23:59:59`,
      reason: reason.trim() || "Leave",
    };
    if (reportingSeniorId.trim()) {
      payload.reporting_senior_id = reportingSeniorId.trim();
    }
    leaveApply(payload);
  };

  return (
    <VStack align="stretch" spacing={8}>
      <Heading size="lg">Leave</Heading>

      {!essDashboardIsLoading && !canApplyLeave && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="600">Leave isn’t available</Text>
            <Text fontSize="sm" mt={1}>
              Your employer has turned off leave applications in the portal for now. Contact HR.
            </Text>
          </Box>
        </Alert>
      )}

      {!essDashboardIsLoading && !leaveTypeId && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="600">No “Leave” entry type</Text>
            <Text fontSize="sm" mt={1}>
              HR must configure a calendar entry type named <strong>Leave</strong> before you can apply here.
            </Text>
          </Box>
        </Alert>
      )}

      <Card shadow="sm" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <Heading size="md">Request leave</Heading>
        </CardHeader>
        <CardBody pt={0}>
          <Box as="form" onSubmit={handleSubmit} maxW="lg">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Start date</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  borderRadius="md"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>End date</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  borderRadius="md"
                />
              </FormControl>
            </SimpleGrid>
            <FormControl mt={4}>
              <FormLabel>Approver (optional)</FormLabel>
              <Select
                placeholder="Use my default reporting manager from HR"
                value={reportingSeniorId}
                onChange={(e) => setReportingSeniorId(e.target.value)}
                borderRadius="md"
                isDisabled={!canApplyLeave || reportingSeniorsIsLoading}
              >
                {reportingSeniors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Pick someone only if you need a different approver than your profile default.
              </Text>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Reason</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional"
                borderRadius="md"
                rows={3}
              />
            </FormControl>
            <Button
              type="submit"
              colorScheme="blue"
              mt={6}
              isLoading={leaveApplyIsLoading}
              isDisabled={!canApplyLeave || !leaveTypeId || !startDate || !endDate}
            >
              Submit request
            </Button>
          </Box>
        </CardBody>
      </Card>

      <Card shadow="sm" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <HStack justify="space-between" align="center" flexWrap="wrap" gap={2}>
            <Heading size="md">My requests</Heading>
            <Button size="xs" variant="ghost" onClick={() => refetchLeaveRequests()}>
              Refresh
            </Button>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          {leaveRequestsIsLoading ? (
            <Text fontSize="sm" color="gray.600">
              Loading…
            </Text>
          ) : leaveRequests.length === 0 ? (
            <Text fontSize="sm" color="gray.600">
              No leave requests yet.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Type</Th>
                    <Th>Start</Th>
                    <Th>End</Th>
                    <Th>Approver</Th>
                    <Th>Reminders</Th>
                    <Th>Status</Th>
                    <Th>Reason</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leaveRequests.map((row) => {
                    const rc = row.reminder_count ?? 0;
                    const lastR = formatReminderAt(row.last_reminder_at);
                    const st = String(row.status || "").trim().toLowerCase();
                    const canRemind = st === "pending";
                    const canRecall = st === "approved";
                    const remindBusyElsewhere =
                      remindMutation.isPending &&
                      remindMutation.variables !== row.leave_id;
                    const recallBusyElsewhere =
                      recallMutation.isPending &&
                      recallMutation.variables !== row.leave_id;

                    return (
                      <Tr key={row.leave_id}>
                        <Td>{row.entry_type}</Td>
                        <Td fontSize="sm">{formatCellDate(row.leave_start)}</Td>
                        <Td fontSize="sm">{formatCellDate(row.leave_end)}</Td>
                        <Td fontSize="sm">{row.assigned_reporting_senior_name || "—"}</Td>
                        <Td fontSize="sm">
                          <VStack align="start" spacing={0}>
                            <Badge colorScheme={rc > 0 ? "blue" : "gray"} variant="subtle">
                              {rc}
                            </Badge>
                            {lastR && (
                              <Text fontSize="xs" color="gray.500" noOfLines={2}>
                                {lastR}
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={statusColor(row.status)}>{row.status}</Badge>
                        </Td>
                        <Td fontSize="sm" maxW="160px" noOfLines={2}>
                          {row.reason || "—"}
                        </Td>
                        <Td>
                          <HStack spacing={2} flexWrap="wrap">
                            {canRemind && (
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="blue"
                                leftIcon={<FiBell />}
                                onClick={() => remindMutation.mutate(row.leave_id)}
                                isLoading={
                                  remindMutation.isPending &&
                                  remindMutation.variables === row.leave_id
                                }
                                isDisabled={
                                  recallMutation.isPending || remindBusyElsewhere
                                }
                              >
                                Remind
                              </Button>
                            )}
                            {canRecall && (
                              <Button
                                size="xs"
                                variant="outline"
                                leftIcon={<FiRotateCcw />}
                                onClick={() => recallMutation.mutate(row.leave_id)}
                                isLoading={
                                  recallMutation.isPending &&
                                  recallMutation.variables === row.leave_id
                                }
                                isDisabled={
                                  remindMutation.isPending || recallBusyElsewhere
                                }
                              >
                                Recall
                              </Button>
                            )}
                            {!canRemind && !canRecall && (
                              <Text fontSize="xs" color="gray.400">
                                —
                              </Text>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
};

export default EssLeave;
